// The company behind a running video ad. The ad server names it with a `companyIdentifier` in the
// VAST; the display name and logo come from our own database, because the ad server carries neither.

export interface AdCompany {
  companyIdentifier: string
  name: string
  logoUrl: string | null
}

/** The running ad as the VAST describes it; the company behind it is looked up with `fetchAdCompany`. */
export interface AdContext {
  companyIdentifier: string
  clickThroughUrl: string
  creativeId: string | null
  /** Only if the ad server passes it as an ad parameter; the IMA SDK does not expose the line item. */
  lineItemId: string | null
}

export const COMPANY_IDENTIFIER_PARAMETER = 'companyIdentifier'
export const LINE_ITEM_ID_PARAMETER = 'lineItemId'

/**
 * Never rejects: an unknown identifier, a network error and a blocked request all give null.
 * `urlTemplate` is the advertisement URL with a `:company_identifier` placeholder, on the edge host where
 * there is one, so the page does not hard-code which host serves it.
 */
export async function fetchAdCompany(
  companyIdentifier: string,
  urlTemplate: string,
): Promise<AdCompany | null> {
  try {
    const url = urlTemplate.replace(':company_identifier', encodeURIComponent(companyIdentifier))
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
    })

    if (!response.ok) {
      return null
    }

    const { name, logo_url: logoUrl } = (await response.json()) as {
      name?: string
      logo_url?: string | null
    }

    return name ? { companyIdentifier, name, logoUrl: logoUrl ?? null } : null
  } catch {
    return null
  }
}
